import type {
  AppointmentId,
  SchedulingTimezone,
  StaffOperationsSnapshot,
  StaffOperationsView,
  StaffVisitSummary,
  VisitId,
  VisitStatus,
  VisitTransitionRequest,
} from "@whitehaven/contracts";
import type {
  AuthorizationSubject,
  Permission,
  PortalCapabilityArea,
  Role,
} from "@whitehaven/permissions";

export const staffOperationsRoles = [
  "SCHEDULER",
  "FRONT_DESK",
  "STAFF",
  "CRM_AGENT",
] as const satisfies readonly Role[];

export function hasStaffOperationsRole(
  subject: AuthorizationSubject | null,
): boolean {
  return (
    subject !== null &&
    subject.roles.some((role) => staffOperationsRoles.includes(role as never))
  );
}

export type StaffOperationsRoute = {
  readonly kind: "operations";
  readonly view: StaffOperationsView;
  readonly appointmentId?: AppointmentId;
  readonly visitId?: VisitId;
};

const exactRoutes: Readonly<Record<string, StaffOperationsRoute>> = {
  "/staff": { kind: "operations", view: "HOME" },
  "/staff/today": { kind: "operations", view: "TIMELINE" },
  "/staff/today/list": { kind: "operations", view: "LIST" },
  "/staff/today/resources": {
    kind: "operations",
    view: "RESOURCE_BOARD",
  },
  "/staff/check-in": { kind: "operations", view: "CHECK_IN_QUEUE" },
  "/staff/checkout": { kind: "operations", view: "CHECKOUT_QUEUE" },
  "/staff/print": { kind: "operations", view: "PRINT_QUEUE" },
};

export function resolveStaffOperationsRoute(
  pathname: string,
): StaffOperationsRoute | null {
  const normalized = pathname.split(/[?#]/, 1)[0] || "/";
  const exact = exactRoutes[normalized];
  if (exact) return exact;
  const selectorPattern = /^[A-Za-z0-9:_-]{3,160}$/;
  const appointment = normalized.match(
    /^\/staff\/appointments\/([^/]+)$/u,
  )?.[1];
  if (appointment && selectorPattern.test(appointment)) {
    return {
      kind: "operations",
      view: "TIMELINE",
      appointmentId: appointment as AppointmentId,
    };
  }
  const printVisit = normalized.match(
    /^\/staff\/visits\/([^/]+)\/print$/u,
  )?.[1];
  if (printVisit && selectorPattern.test(printVisit)) {
    return {
      kind: "operations",
      view: "PRINT_QUEUE",
      visitId: printVisit as VisitId,
    };
  }
  const visit = normalized.match(/^\/staff\/visits\/([^/]+)$/u)?.[1];
  if (visit && selectorPattern.test(visit)) {
    return {
      kind: "operations",
      view: "TIMELINE",
      visitId: visit as VisitId,
    };
  }
  const checkoutVisit = normalized.match(/^\/staff\/check-out\/([^/]+)$/u)?.[1];
  if (checkoutVisit && selectorPattern.test(checkoutVisit)) {
    return {
      kind: "operations",
      view: "CHECKOUT_QUEUE",
      visitId: checkoutVisit as VisitId,
    };
  }
  return null;
}

export function staffOperationsCapabilityArea(
  view: StaffOperationsView,
): PortalCapabilityArea {
  if (view === "HOME") return "staff-overview";
  if (view === "CHECK_IN_QUEUE") return "staff-check-in";
  if (view === "CHECKOUT_QUEUE") return "staff-checkout";
  if (view === "PRINT_QUEUE") return "staff-print";
  return "staff-today";
}

export function createStaffOperationsProjectionKey({
  userId,
  locationId,
  locationTimezone,
  date,
  appointmentId,
  visitId,
  view,
}: {
  readonly userId: string | null;
  readonly locationId: string | null;
  readonly locationTimezone: SchedulingTimezone | null;
  readonly date: string;
  readonly appointmentId: string | null;
  readonly visitId: string | null;
  readonly view: StaffOperationsView;
}): string {
  return JSON.stringify([
    userId,
    locationId,
    locationTimezone,
    date,
    appointmentId,
    visitId,
    view,
  ]);
}

function localDateForStaffVisit(
  visit: StaffVisitSummary,
  trustedLocationTimezone: SchedulingTimezone,
): string | null {
  const startsAt = new Date(visit.startsAt);
  if (Number.isNaN(startsAt.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      calendar: "gregory",
      day: "2-digit",
      month: "2-digit",
      numberingSystem: "latn",
      timeZone: trustedLocationTimezone,
      year: "numeric",
    }).formatToParts(startsAt);
    const part = (type: "year" | "month" | "day") =>
      parts.find((item) => item.type === type)?.value;
    const year = part("year");
    const month = part("month");
    const day = part("day");
    return year && month && day ? `${year}-${month}-${day}` : null;
  } catch {
    return null;
  }
}

export function matchesStaffOperationsSnapshotQuery(
  snapshot: StaffOperationsSnapshot,
  query: {
    readonly date: string;
    readonly locationId: string;
    readonly locationTimezone: SchedulingTimezone;
    readonly appointmentId: AppointmentId | null;
    readonly visitId: VisitId | null;
  },
): boolean {
  return (
    snapshot.date === query.date &&
    snapshot.locationId === query.locationId &&
    snapshot.locationTimezone === query.locationTimezone &&
    snapshot.visits.every(
      (visit) =>
        visit.locationId === query.locationId &&
        visit.timezone === query.locationTimezone &&
        localDateForStaffVisit(visit, query.locationTimezone) === query.date &&
        (query.appointmentId === null ||
          visit.appointmentId === query.appointmentId) &&
        (query.visitId === null || visit.visitId === query.visitId),
    )
  );
}

export const staffOperationsLinks = [
  { href: "/staff", label: "Home", view: "HOME" },
  { href: "/staff/today", label: "Timeline", view: "TIMELINE" },
  { href: "/staff/today/list", label: "List", view: "LIST" },
  {
    href: "/staff/today/resources",
    label: "Resource board",
    view: "RESOURCE_BOARD",
  },
  { href: "/staff/check-in", label: "Check in", view: "CHECK_IN_QUEUE" },
  { href: "/staff/checkout", label: "Checkout", view: "CHECKOUT_QUEUE" },
  { href: "/staff/print", label: "Print", view: "PRINT_QUEUE" },
] as const;

export function operationForStatus(
  status: VisitStatus,
): VisitTransitionRequest["kind"] | null {
  if (status === "SCHEDULED") return "CHECK_IN";
  if (status === "CHECKED_IN") return "START";
  if (status === "IN_PROGRESS") return "COMPLETE";
  if (status === "COMPLETED") return "BEGIN_CHECKOUT";
  if (status === "CHECKOUT_PENDING") return "CHECK_OUT";
  return null;
}

export function selectCurrentAndNextStaffVisits(
  visits: readonly StaffVisitSummary[],
  now: string,
): {
  readonly current: StaffVisitSummary | null;
  readonly next: StaffVisitSummary | null;
} {
  if (!Number.isFinite(Date.parse(now))) {
    return { current: null, next: null };
  }
  const active = visits
    .filter(
      (visit) =>
        !["CANCELLED", "NO_SHOW", "CHECKED_OUT"].includes(visit.status),
    )
    .sort(
      (left, right) =>
        left.startsAt.localeCompare(right.startsAt) ||
        String(left.visitId).localeCompare(String(right.visitId)),
    );
  const priority: Readonly<Record<VisitStatus, number>> = {
    IN_PROGRESS: 0,
    CHECKED_IN: 1,
    COMPLETED: 2,
    CHECKOUT_PENDING: 3,
    SCHEDULED: 4,
    CHECKED_OUT: 5,
    NO_SHOW: 6,
    CANCELLED: 7,
  };
  const current =
    active
      .filter(
        (visit) =>
          [
            "CHECKED_IN",
            "IN_PROGRESS",
            "COMPLETED",
            "CHECKOUT_PENDING",
          ].includes(visit.status) ||
          (visit.startsAt <= now && visit.endsAt > now),
      )
      .sort(
        (left, right) =>
          priority[left.status] - priority[right.status] ||
          left.startsAt.localeCompare(right.startsAt),
      )[0] ?? null;
  const next =
    active.find(
      (visit) => visit.visitId !== current?.visitId && visit.startsAt > now,
    ) ?? null;
  return { current, next };
}

export function permissionForOperation(
  route: StaffOperationsRoute,
): Permission {
  return route.view === "HOME" ||
    route.view === "TIMELINE" ||
    route.view === "LIST" ||
    route.view === "RESOURCE_BOARD" ||
    route.view === "PRINT_QUEUE"
    ? "visit.assigned.read"
    : "visit.operations.write";
}
